"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import { Section, Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";

export interface TabStepperStep {
  number: string;
  title: string;
  description: React.ReactNode;
  /** Optional per-step video; overrides section-level `videoSrc` when set. */
  videoSrc?: string;
  /**
   * Optional H.264/MP4 fallback for browsers that cannot play WebM
   * (notably iOS Safari). Prefer providing both when available.
   */
  videoSrcMp4?: string;
  /** Optional static image shown when no video is available for this step. */
  image?: string;
  imageAlt?: string;
}

interface TabStepperSectionProps {
  id?: string;
  label?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  steps: TabStepperStep[];
  /** Optional call to action rendered below the steps. */
  cta?: {
    href: string;
    label: string;
    target?: string;
  };
  className?: string;
  /** Fallback video when a step does not define its own `videoSrc`. */
  videoSrc?: string;
  /** Fallback MP4 when a step does not define its own `videoSrcMp4`. */
  videoSrcMp4?: string;
}

type StepVideoProps = {
  src?: string;
  srcMp4?: string;
  active: boolean;
  label: string;
  /** Gates the actual network fetch until the section is near the viewport. */
  canLoad: boolean;
};

/**
 * Plays muted inline video when `active` becomes true.
 * Toggling the `autoPlay` attribute alone does not restart playback on
 * already-mounted mobile accordion videos.
 */
function StepVideo({ src, srcMp4, active, label, canLoad }: StepVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (!active || !canLoad) {
      el.pause();
      return;
    }

    const tryPlay = () => {
      const playPromise = el.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          /* Autoplay can still be blocked; user gesture opens the accordion. */
        });
      }
    };

    tryPlay();
    el.addEventListener("loadeddata", tryPlay);
    return () => el.removeEventListener("loadeddata", tryPlay);
  }, [active, canLoad, src, srcMp4]);

  if (!src && !srcMp4) return null;

  return (
    <video
      ref={videoRef}
      autoPlay={active && canLoad}
      loop
      muted
      playsInline
      preload={canLoad && active ? "auto" : "none"}
      aria-label={label}
      className="block w-full h-auto"
    >
      {canLoad && src ? <source src={src} type="video/webm" /> : null}
      {canLoad && srcMp4 ? <source src={srcMp4} type="video/mp4" /> : null}
    </video>
  );
}

export function TabStepperSection({
  id,
  label = "Getting Started",
  title,
  subtitle,
  steps,
  cta,
  className = "",
  videoSrc,
  videoSrcMp4,
}: TabStepperSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  // The step videos are multi-megabyte webms sitting well below the fold. Only
  // start fetching them once the section is close to the viewport, so they stop
  // competing with the hero for bandwidth on first load.
  const [canLoadVideos, setCanLoadVideos] = useState(false);
  const videoAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (canLoadVideos) return;
    if (typeof IntersectionObserver === "undefined") {
      setCanLoadVideos(true);
      return;
    }

    let observer: IntersectionObserver | undefined;
    // Wait for `load` before even watching, so the videos can never compete with
    // the hero for bandwidth — this section sits well over a viewport down.
    const startObserving = () => {
      const el = videoAreaRef.current;
      if (!el) return;
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setCanLoadVideos(true);
            observer?.disconnect();
          }
        },
        { rootMargin: "300px 0px" }
      );
      observer.observe(el);
    };

    if (document.readyState === "complete") {
      startObserving();
      return () => observer?.disconnect();
    }
    window.addEventListener("load", startObserving, { once: true });
    return () => {
      window.removeEventListener("load", startObserving);
      observer?.disconnect();
    };
  }, [canLoadVideos]);

  const activeStep = steps[activeIndex];
  const activeVideoSrc = activeStep.videoSrc ?? videoSrc;
  const activeVideoSrcMp4 = activeStep.videoSrcMp4 ?? videoSrcMp4;
  const hasActiveVideo = Boolean(activeVideoSrc || activeVideoSrcMp4);
  const hasActiveVisual = hasActiveVideo || Boolean(activeStep.image);

  return (
    <Section id={id} variant="default" className={className}>
      <div className="mb-14 lg:mb-16 text-center max-w-4xl mx-auto">
        <span className="section-label">{label}</span>
        <Typography variant="h2" as="h2" className="mb-4 text-4xl font-bold">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body" as="p" className="text-text-secondary">
            {subtitle}
          </Typography>
        ) : null}
      </div>

      <div ref={videoAreaRef}>
      {/* Desktop — horizontal tab strip + side-by-side video/content */}
      <div className="hidden md:block">
        <div className="flex justify-center gap-8 md:gap-10 border-b border-gray-200 overflow-x-auto scrollbar-hide">
          {steps.map((step, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={step.number + step.title}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-pressed={isActive}
                className={`shrink-0 flex items-baseline gap-2 pb-4 -mb-px border-b-2 transition-colors ${
                  isActive ? "border-primary" : "border-transparent"
                }`}
              >
                <span
                  className={`text-sm font-semibold ${isActive ? "text-primary" : "text-gray-400"}`}
                >
                  {String(index + 1).padStart(2, "0")}.
                </span>
                <Typography
                  as="span"
                  variant="h5"
                  className={`font-bold whitespace-nowrap ${
                    isActive ? "text-text-primary" : "text-gray-400"
                  }`}
                >
                  {step.title}
                </Typography>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-[3fr_2fr] gap-14 items-center mt-10">
          {hasActiveVideo ? (
            <div className="relative overflow-hidden border border-gray-200 shadow-sm bg-gray-100">
              <StepVideo
                key={`${activeVideoSrc ?? ""}-${activeVideoSrcMp4 ?? ""}`}
                src={activeVideoSrc}
                srcMp4={activeVideoSrcMp4}
                active
                canLoad={canLoadVideos}
                label={`${activeStep.title} — Pickcel digital signage demo`}
              />
            </div>
          ) : activeStep.image ? (
            <div className="relative overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-gray-100">
              <Image
                src={activeStep.image}
                alt={activeStep.imageAlt ?? activeStep.title}
                width={840}
                height={560}
                className="w-full h-auto object-cover"
              />
            </div>
          ) : null}

          <div className={hasActiveVisual ? "" : "col-span-2 max-w-2xl mx-auto text-center"}>
            <span className="text-primary/25 text-7xl font-black leading-none tracking-tight">
              {activeStep.number}
            </span>
            <Typography variant="h3" as="h3" className="text-text-primary font-bold mt-4 mb-3">
              {activeStep.title}
            </Typography>
            <Typography variant="body" as="p" className="text-text-secondary leading-relaxed max-w-[60ch]">
              {activeStep.description}
            </Typography>
          </div>
        </div>
      </div>

      {/* Mobile — collapsed accordion list, each step with its own video when expanded */}
      <div className="md:hidden">
        <div className="flex flex-col divide-y divide-gray-200 border-y border-gray-200">
          {steps.map((step, index) => {
            const isActive = index === activeIndex;
            const stepVideoSrc = step.videoSrc ?? videoSrc;
            const stepVideoSrcMp4 = step.videoSrcMp4 ?? videoSrcMp4;
            return (
              <div key={step.number + step.title}>
                <button
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-expanded={isActive}
                  aria-controls={`mobile-step-panel-${index}`}
                  className="flex w-full items-center gap-4 py-5 text-left"
                >
                  <span
                    className={`text-sm font-semibold shrink-0 ${
                      isActive ? "text-primary" : "text-gray-400"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <Typography
                    as="span"
                    variant="h5"
                    className={`flex-1 font-bold ${isActive ? "text-text-primary" : "text-gray-400"}`}
                  >
                    {step.title}
                  </Typography>
                  <span
                    className={`relative flex h-6 w-6 shrink-0 items-center justify-center transition-transform duration-200 ${
                      isActive ? "rotate-45 text-primary" : "text-gray-400"
                    }`}
                    aria-hidden="true"
                  >
                    <span className="absolute h-[1.5px] w-3.5 bg-current" />
                    <span className="absolute h-3.5 w-[1.5px] bg-current" />
                  </span>
                </button>
                <div
                  id={`mobile-step-panel-${index}`}
                  className="grid overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ gridTemplateRows: isActive ? "1fr" : "0fr" }}
                  aria-hidden={!isActive}
                >
                  <div className="min-h-0 overflow-hidden">
                    {stepVideoSrc || stepVideoSrcMp4 ? (
                      <div className="mb-4 overflow-hidden border border-gray-200 shadow-sm bg-gray-100">
                        <StepVideo
                          src={stepVideoSrc}
                          srcMp4={stepVideoSrcMp4}
                          active={isActive}
                          canLoad={canLoadVideos}
                          label={`${step.title} — Pickcel digital signage demo`}
                        />
                      </div>
                    ) : step.image ? (
                      <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-gray-100">
                        <Image
                          src={step.image}
                          alt={step.imageAlt ?? step.title}
                          width={840}
                          height={560}
                          className="w-full h-auto object-cover"
                        />
                      </div>
                    ) : null}
                    <Typography
                      variant="body"
                      as="p"
                      className="pb-6 text-text-secondary leading-relaxed"
                    >
                      {step.description}
                    </Typography>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>

      {cta ? (
        <div className="mt-12 flex justify-center">
          <Link
            href={cta.href}
            target={cta.target ?? (cta.href.startsWith("http") ? "_blank" : undefined)}
            rel={cta.href.startsWith("http") ? "noreferrer" : undefined}
          >
            <NewButton variant="primary" size="md" arrow="grow">
              {cta.label}
            </NewButton>
          </Link>
        </div>
      ) : null}
    </Section>
  );
}
